"use client";

import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from '@/lib/api';
import { useAuth } from '../context/AuthContext';
import { MoreHorizontal, Edit, Trash2, Plus, UserPlus, Loader2, Shield, User, Eye } from "lucide-react";

export function UsersPage() {
  const { token, user } = useAuth();
  const queryClient = useQueryClient();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [formData, setFormData] = useState({ username: "", password: "", role: "viewer" });

  const { data: users } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.getUsers(token!),
    enabled: !!token,
  });

  const createMutation = useMutation({
    mutationFn: (data: { username: string; password: string; role: string }) => api.createUser(data, token!),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["users"] }); setCreateDialogOpen(false); setFormData({ username: "", password: "", role: "viewer" }); },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => api.updateUser(id, data, token!),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["users"] }); setEditDialogOpen(false); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.deleteUser(id, token!),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["users"] }); },
  });

  const handleEdit = (usr: any) => {
    setSelectedUser(usr);
    setFormData({ username: usr.username, password: "", role: usr.role });
    setEditDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedUser) {
      const { password, ...rest } = formData;
      updateMutation.mutate({ id: selectedUser.id, data: { ...rest, ...(password && { password }) } });
    } else {
      createMutation.mutate(formData);
    }
  };

  const roleOptions = [
    { value: "admin", label: "Admin", description: "Full access to all features" },
    { value: "operator", label: "Operator", description: "Can manage devices, groups, policies" },
    { value: "viewer", label: "Viewer", description: "Read-only access" },
  ];

  const roleColors = { admin: "destructive", operator: "default", viewer: "secondary" } as const;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">User Management</h1>
          <p className="text-muted-foreground">Manage dashboard user accounts and permissions</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild><Button><UserPlus className="mr-2 h-4 w-4" />Add User</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create User</DialogTitle><DialogDescription>Add a new user account</DialogDescription></DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="grid gap-4 py-4">
                <div className="space-y-2"><Label htmlFor="username">Username</Label><Input id="username" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} required /></div>
                <div className="space-y-2"><Label htmlFor="password">Password</Label><Input id="password" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required /></div>
                <div className="space-y-2"><Label htmlFor="role">Role</Label><Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{roleOptions.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => { setCreateDialogOpen(false); setFormData({ username: "", password: "", role: "viewer" }); }}>Cancel</Button><Button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</> : "Create User"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader><CardTitle>Users</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>Username</TableHead><TableHead>Role</TableHead><TableHead>Created</TableHead><TableHead>Last Login</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {users?.map((usr: any) => (
                <TableRow key={usr.id}>
                  <TableCell className="font-medium">{usr.username}</TableCell>
                  <TableCell><Badge variant={roleColors[usr.role as keyof typeof roleColors] as any}>{usr.role}</Badge></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{new Date(usr.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{usr.last_login ? new Date(usr.last_login).toLocaleString() : "Never"}</TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(usr)}><Edit className="mr-2 h-4 w-4" />Edit</DropdownMenuItem>
                        {usr.id !== user?.id && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => deleteMutation.mutate(usr.id)} className="text-destructive focus:text-destructive"><Trash2 className="mr-2 h-4 w-4" />Delete</DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {users?.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No users found</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit User</DialogTitle><DialogDescription>Update user account</DialogDescription></DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2"><Label htmlFor="username">Username</Label><Input id="username" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} required /></div>
              <div className="space-y-2"><Label htmlFor="password">New Password (leave blank to keep current)</Label><Input id="password" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} /></div>
              <div className="space-y-2"><Label htmlFor="role">Role</Label><Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{roleOptions.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => { setEditDialogOpen(false); setFormData({ username: "", password: "", role: "viewer" }); }}>Cancel</Button><Button type="submit" disabled={updateMutation.isPending}>{updateMutation.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : "Save Changes"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}